<template>
  <div class="sticky-wrapper">
    <div class="qmove-container">
      <ul class="scrollspy-bar" ref="scrollSpyBarRef">
        <li v-for="navItem in navItems" :key="navItem.id" :data-id="navItem.id" class="scrollspy-nav-item" @click="(e) => scrollTo(navItem.id, navItem.label)">
          <a :href="'#' + navItem.id" class="no-underline scrollspy-nav-item__link"> {{ navItem.label }}</a>
        </li>
      </ul>
    </div>
  </div>

  <!-- Slot in content for each nav item -->
  <slot>
    <div v-for="navData in navItems" :key="navData.id" :id="navData.id" class="scrollspy-section">
      <h2>{{ navData.label }}</h2>
      <p>Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>
    </div>
  </slot>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useActive } from 'vue-use-active-scroll';
import animateScrollTo from 'animated-scroll-to';
import { useAnalytics } from '../../composables/useAnalytics';
import { useUrlBuilder } from '../../composables/useUrlBuilder';
import { AnalyticsEvents, AnalyticsEventTypes } from '../../foundation/enums/AnalyticsEvents';

type NavItemType = {
  id: string;
  label: string;
};

const props = defineProps<{
  navItems: NavItemType[];
  carData: any;
}>();

const analytics = useAnalytics();
const initialOffset = ref(0);
const targets = computed(() => [...props.navItems].map(({ id }) => id));
const { setActive, activeId } = useActive(targets, { boundaryOffset: { toBottom: 0 } });
const scrollSpyBarRef = ref<HTMLElement | null>(null);
const SCROLLSPY_OFFSET = 200;

/** Push data to data layer for analytics */
function pushToAnalytics(id: string) {
  try {
    analytics.push(AnalyticsEvents.NavigationInPageTabClick, {
      eventInfo: {
        navigation: {
          linkClicks: 1,
          name: id,
          type: AnalyticsEventTypes.Tabs,
          makeName: props.carData.Make,
          modelName: props.carData.Model,
          badgeName: props.carData.Trim,
          year: `${props.carData.Year_low} - ${props.carData.Year_high}`,
          bodyType: props.carData.Body_type,
          engineType: props.carData.Overall_EngineType,
          URL: useUrlBuilder().getCarDetailsUrl(
            props.carData.Vehicle_UID,
            `${props.carData.Year_low} - ${props.carData.Year_high}`,
            props.carData.Make,
            props.carData.Model
          ),
        },
      },
    });
  } catch (error) {
    console.warn('Fail to push data to analytics', error);
  }
}

/**
 * Scroll to the correct element on the page via 'id'
 * @param id
 */
function scrollTo(id: string, label: string) {
  setActive(id);
  const elementToScrollTo = document.getElementById(id);
  pushToAnalytics(label);

  if (elementToScrollTo) {
    animateScrollTo(elementToScrollTo as HTMLElement, {
      minDuration: 300,
      maxDuration: 600,
      verticalOffset: -100,
    });
  }
}

// Bit hacky, but the useMonitorElementVisibility hook just wasn't cutting it.
// This allows the sticky header to stick at the correct point on the page.
// watch(activeId, (val) => (isVisible.value = Boolean(val)), { immediate: true });

// If on mobile, scroll to the active nav element otherwise it is active but out of view.
// For example, if I scroll down the page far enough, the active scroll spy element won't
// be visible because it's too far right in the viewport.
watch(
  activeId,
  (val) => {
    const activeElement = scrollSpyBarRef.value?.querySelector('.active');

    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  },
  { immediate: true, flush: 'post' }
);

onMounted(() => {
  const stickyEl = document.querySelector('.sticky-wrapper');

  if (stickyEl !== null) {
    initialOffset.value = (stickyEl as HTMLElement).offsetTop;
  }

  window.addEventListener('scroll', handleStickyScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleStickyScroll);
});

/**
 * Sticky scroll implementation has to be vanilla JS so that
 * it works outside the Vue app for SXA components.
 */
function handleStickyScroll() {
  const navbar = document.querySelector('.sticky-wrapper');

  if (navbar !== null) {
    if (window.pageYOffset >= initialOffset.value) {
      navbar.classList.add('sticky');
    } else {
      navbar.classList.remove('sticky');
    }
  }

  handleActiveMenu();
}

function handleActiveMenu() {
  const menuLinks = document.querySelectorAll('.sticky-wrapper .scrollspy-nav-item');
  const sectionsById: any = document.querySelectorAll('[data-scrollspy-id]');
  const menuLinksArray: any[] = Array.from(menuLinks);

  menuLinksArray.forEach((menu) => {
    menu.classList.remove('active');
  });

  sectionsById.forEach((item: any) => {
    const offset = SCROLLSPY_OFFSET;
    const scroll = window.pageYOffset;

    const clamp = (value: number) => Math.max(0, value);
    const isBetween = (value: number, floor: number, ceil: number) => value >= floor && value <= ceil;

    const sectionPositions = Array.from(sectionsById).map((element: any) => {
      if (!element) return { element, top: -1, bottom: -1 };

      const rect = element.getBoundingClientRect();
      const top = clamp(rect.top + scroll - offset);
      const bottom = clamp(rect.bottom + scroll - offset);

      return {
        id: element.id,
        top,
        bottom,
      };
    });

    const matchingSection = sectionPositions.find(({ top, bottom }) => isBetween(scroll, top, bottom));

    if (matchingSection?.id) {
      const matchingNavItem = menuLinksArray.find((link) => link.dataset.id === matchingSection.id);
      matchingNavItem.classList.add('active');
    }
  });
}
</script>
<style lang="scss" scoped>
.sticky-wrapper {
  width: 100vw;
  z-index: 1000;
  background: #fff;
  margin-left: calc(-50vw + 50%);
  box-shadow: inset 0 -5px 0 0 $racq-pale-blue-border;
}

.sticky {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
}

.scrollspy-bar {
  background: #ffffff;
  box-shadow: inset 0 -5px 0 0 $racq-pale-blue-border;
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
  list-style: none;
  padding: 0 1rem;
  overflow-x: scroll;

  @media (min-width: $breakpoint-xl) {
    overflow-x: hidden;
  }

  @media (min-width: $breakpoint-xxl) {
    padding: 0;
  }

  li:hover {
    background: $racq-pale-blue;
  }
}

.scrollspy-nav-item {
  padding: 1.6rem;
  text-decoration: none;
  font-weight: bold;
  min-width: fit-content;
  position: relative;
  cursor: pointer;

  &__link {
    color: $racq-cool-grey;
    text-transform: lowercase;
  }

  &::first-letter {
    text-transform: uppercase;
  }

  &.active {
    box-shadow: inset 0 -5px 0 0 $racq-sky-blue;
  }
}
</style>
